/**
 * Helpers for handling arrays.
 */
export class Arrays {

    /**
     * Takes in an array or string or null and always returns an array.
     * If the incoming value is a string, splits it by commas. This is especaially useful for HTML attributes.
     * Note that null (or other falsy objects) are returned as an empty array.
     *
     * @param arrayCandidate - A string array or string that is split by comma.
     */
    public static assureArray(arrayCandidate: string | string[]): string[] {
        if (!arrayCandidate) {
            return [];
        }
        if (Array.isArray(arrayCandidate)) {
            return arrayCandidate;
        }
        return arrayCandidate.split(",");
    }

    /**
     * Calculates a sum of array items.
     * Null items will be added as zero.
     * @param array Array to sum
     * @param selector Selector for a number to sum.
     */
    public static sum<T>(array: T[], selector: (item: T) => number): number {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return array.reduce((prev, current, ix) => current ? (prev + (selector(current) || 0)) : 0, 0);
    }

    /**
     * Sequence generator function gets an array between two numbers (inclusive).
     * (commonly referred to as "range", e.g. Clojure, PHP etc)
     * @param start Start number of the sequence
     * @param stop End number of the sequence
     * @param step Step between the numbers, default is 1.
     * @example
     * Arrays.getRange(2019, 2023);
     * // [2019, 2020, 2021, 2022, 2023]
     */
    public static getRange(start: number, stop: number, step = 1): number[] {
      const range = (start: number, stop: number, step: number) =>
        Array.from({ length: (stop - start) / step + 1}, (_, i) => start + (i * step));
      return range(start, stop, step);
    }

    /**
     * Does a group-by of an array of objects with given key.
     * @param source The source array.
     * @param selector Selector for the key in the array.
     * @returns An object that is the array grouped by the keys.
     */
    public static groupBy<T>(source: T[], selector: (x:T) => string | undefined | null): ({ [key: string]: T[]}) {
      return source.reduce((previous, current) => {
        const key = selector(current);
        if (key !== undefined && key !== null) {
          (previous[key] ??= []).push(current);
        }
        return previous;
      }, {});
    }

    /**
     * Returns distinct elements from an array based on a selector function.
     * @param source The source array to filter
     * @param selector Function to select the key to compare for uniqueness
     * @returns Array with duplicate elements removed
     */
    public static distinct<T>(source: T[], selector: (x:T) => string): T[] {
      return source.filter((x, ix, self) => self.findIndex(y => selector(x) === selector(y)) === ix);
    }

    /** Moves an item of given index one position up or down in the array */
    public static moveItem<T>(array: T[], index: number, direction: "up" | "down"):void {
      if (direction === "up" && index > 0) {
        this.moveItemInArray(array, index, index - 1);
      } else if (direction === "down" && index < (array.length - 1)) {
        this.moveItemInArray(array, index, index + 1);
      }
    }

    private static moveItemInArray<T>(array: T[], fromIndex: number, toIndex: number): void {
      const element = array[fromIndex];
      array.splice(fromIndex, 1);
      array.splice(toIndex, 0, element);
    }
}
